import {
  parseTaskId,
  customerChangeIsSaved,
  assigneeChangeIsSaved,
} from '../screens/TaskBoard/taskUtils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { io, Socket } from 'socket.io-client';
import { nanoid } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../store/store';
import {
  fetchTasks,
  fetchTaskById,
  fetchTaskHistory,
  readTaskSnapshot,
  readTaskHistorySnapshot,
  startOptimisticTaskStatus,
  confirmOptimisticTaskStatus,
  rollbackOptimisticTaskStatus,
  Task,
  TaskUpdatePayloadRequest,
  TaskAssignPayloadRequest,
  TASK_STATUSES,
  TASK_PRIORITIES,
  CommentCreatePayload,
} from '../store/slices/tasks';

type TaskChange = TaskUpdatePayloadRequest | TaskAssignPayloadRequest;
type TaskEventData = { taskId?: unknown; task_id?: unknown };
type TaskError = TaskEventData & { message?: string | string[] };

export interface TaskSocketActions {
  socket: Socket | null;
  connected: boolean;
  connectionError: string;
  pendingTaskIds: number[];
  setSelectedTaskId: (id: number | undefined) => void;
  updateTask: (payload: TaskUpdatePayloadRequest) => Promise<void>;
  assignTask: (payload: TaskAssignPayloadRequest) => Promise<void>;
  createComment: (payload: CommentCreatePayload) => Promise<void>;
}

export const TaskContext = createContext<TaskSocketActions | null>(null);

// REST uses different field names from the socket request schema.
const changeIsSaved = (task: Task, change: TaskChange) => {
  if ('person' in change) return false; // Assignment IDs also belong to the task service.
  if (change.customer) return customerChangeIsSaved(task, change.customer);
  if ('status' in change) return task.status?.toLowerCase().replace(/_/g, ' ') === change.status;
  if ('priority' in change) return task.priority === change.priority;
  if ('title' in change) return task.title === change.title;
  if ('description' in change) return (task.description ?? '') === change.description;
  const dates = [
    [change.datetimePlaned, task.datetime_planned],
    [change.datetimeStart, task.datetime_start],
    [change.datetimeFinish, task.datetime_finish],
  ];
  return dates.some(
    ([requested, saved]) =>
      requested !== undefined &&
      ((!requested && !saved) || new Date(requested).getTime() === new Date(saved ?? '').getTime())
  );
};

export const TaskContextProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<number>();
  const [pendingTaskIds, setPendingTaskIds] = useState<number[]>([]);
  const pendingChange = useRef<{
    taskId: number;
    controller: AbortController;
    error?: Error;
  } | null>(null);

  const refreshTasks = useCallback(
    (data?: TaskEventData) => {
      dispatch(fetchTasks());
      const id = parseTaskId(data?.task_id ?? data?.taskId);
      if (selectedTaskId != null && (id == null || id === selectedTaskId)) {
        dispatch(fetchTaskById(selectedTaskId));
        dispatch(fetchTaskHistory(selectedTaskId));
      }
    },
    [dispatch, selectedTaskId]
  );

  // Setup the socket, as in ChatContext.
  useEffect(() => {
    const origin = window.location.origin;
    const newSocket = io(`${origin.replace(/\/$/, '')}/socket`, {
      path: '/task/ws',
      withCredentials: true,
      autoConnect: false,
    });
    setSocket(newSocket);
    return () => {
      if (pendingChange.current) {
        pendingChange.current.error = new Error('Task connection closed.');
        pendingChange.current.controller.abort();
        pendingChange.current = null;
      }
      newSocket.disconnect();
    };
  }, []);

  // Receive task events and connection errors.
  useEffect(() => {
    if (!socket) return;
    const onConnect = () => {
      setConnected(true);
      setConnectionError('');
      refreshTasks();
    };
    const onError = (data: TaskError) => {
      const message = Array.isArray(data?.message)
        ? data.message.join(', ')
        : data?.message || 'The task operation was rejected.';
      const id = parseTaskId(data?.task_id ?? data?.taskId);
      const operation = pendingChange.current;
      if (operation && (id == null || operation.taskId === id)) {
        operation.error = new Error(message);
        operation.controller.abort();
      } else {
        toast.error(message, { position: toast.POSITION.BOTTOM_RIGHT });
      }
    };
    const onDisconnect = () => {
      setConnected(false);
      setConnectionError('Task connection disconnected.');
      if (pendingChange.current) onError({ message: 'Task connection disconnected.' });
    };
    const onConnectError = (error: Error) => {
      setConnected(false);
      setConnectionError(error.message || 'Unable to connect to the task service.');
      if (pendingChange.current) onError(error);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('task:create', refreshTasks);
    socket.on('task:update', refreshTasks);
    socket.on('task:assign', refreshTasks);
    socket.on('comment:create', refreshTasks);
    socket.on('comment:update', refreshTasks);
    socket.on('comment:delete', refreshTasks);
    socket.on('task:error', onError);
    socket.on('exception', onError);
    socket.connect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('task:create', refreshTasks);
      socket.off('task:update', refreshTasks);
      socket.off('task:assign', refreshTasks);
      socket.off('comment:create', refreshTasks);
      socket.off('comment:update', refreshTasks);
      socket.off('comment:delete', refreshTasks);
      socket.off('task:error', onError);
      socket.off('exception', onError);
    };
  }, [socket, refreshTasks]);

  // The gateway excludes the sender from broadcasts, so check the saved task.
  const confirmChange = async (change: TaskChange, previousHistoryIds?: Set<string>) => {
    if (pendingChange.current) throw new Error('Wait for the current task change to finish.');
    const taskId = parseTaskId(change.taskId);
    if (taskId == null) throw new Error('A valid task ID is required.');
    const operation = {
      taskId,
      controller: new AbortController(),
      error: undefined as Error | undefined,
    };
    pendingChange.current = operation;
    setPendingTaskIds([taskId]);
    const timeout = setTimeout(() => {
      operation.error = new Error(
        'The task service did not confirm the change. Refresh to check its status.'
      );
      operation.controller.abort();
      refreshTasks({ taskId });
    }, 10000);
    try {
      while (!operation.error) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (operation.error) throw operation.error;
        try {
          const task: Task = await readTaskSnapshot(taskId, operation.controller.signal);
          if (operation.error) throw operation.error;
          let saved = false;
          if (previousHistoryIds && 'person' in change) {
            const history = await readTaskHistorySnapshot(taskId, operation.controller.signal);
            saved = assigneeChangeIsSaved(task, change.person, history, previousHistoryIds);
          } else {
            saved = changeIsSaved(task, change);
          }
          if (operation.error) throw operation.error;
          if (task.task_id === taskId && saved) {
            return task;
          }
        } catch (error: unknown) {
          if (operation.error) throw operation.error;
          const status = (error as { response?: { status?: number } })?.response?.status;
          if (status === 401 || status === 403)
            throw new Error('Not authorized to verify the task change.');
        }
      }
      throw operation.error;
    } finally {
      clearTimeout(timeout);
      if (pendingChange.current === operation) {
        pendingChange.current = null;
        setPendingTaskIds([]);
      }
    }
  };

  // Send task changes directly with Socket.IO, as chat does.
  const updateTask = async (payload: TaskUpdatePayloadRequest) => {
    if (!socket?.connected) throw new Error('Connect to the task service before making changes.');
    const taskId = parseTaskId(payload.taskId);
    if (taskId == null) throw new Error('A valid task ID is required.');
    if (pendingChange.current) throw new Error('Wait for the current task change to finish.');
    const fields = Object.keys(payload).filter((key) => key !== 'taskId');
    if (fields.length !== 1) throw new Error('Update exactly one task field at a time.');
    if (payload.status !== undefined && !TASK_STATUSES.includes(payload.status))
      throw new Error('Invalid task status.');
    if (payload.priority !== undefined && !TASK_PRIORITIES.includes(payload.priority))
      throw new Error('Invalid task priority.');
    const optimisticRequestId = payload.status !== undefined ? nanoid() : undefined;
    if (optimisticRequestId && payload.status !== undefined)
      dispatch(startOptimisticTaskStatus({ taskId, status: payload.status, requestId: optimisticRequestId }));
    try {
      socket.emit('task:update', { ...payload, taskId });
      const task = await confirmChange({ ...payload, taskId });
      if (optimisticRequestId)
        dispatch(confirmOptimisticTaskStatus({ task, requestId: optimisticRequestId }));
      refreshTasks(task);
    } catch (error) {
      if (optimisticRequestId) {
        dispatch(rollbackOptimisticTaskStatus({ taskId, requestId: optimisticRequestId }));
        refreshTasks({ taskId });
      }
      throw error;
    }
  };

  const assignTask = async (payload: TaskAssignPayloadRequest) => {
    if (!socket?.connected) throw new Error('Connect to the task service before making changes.');
    const taskId = parseTaskId(payload.taskId);
    if (taskId == null) throw new Error('A valid task ID is required.');
    if (pendingChange.current) throw new Error('Wait for the current task change to finish.');
    const previousHistoryIds = new Set(
      (await readTaskHistorySnapshot(taskId))
        .filter((entry) => entry.history_id != null)
        .map((entry) => String(entry.history_id))
    );
    if (!socket.connected) throw new Error('Connect to the task service before making changes.');
    if (pendingChange.current) throw new Error('Wait for the current task change to finish.');
    socket.emit('task:assign', { ...payload, taskId });
    const task = await confirmChange({ ...payload, taskId }, previousHistoryIds);
    refreshTasks(task);
  };

  const createComment = async (payload: CommentCreatePayload) => {
    if (!socket?.connected) throw new Error('Connect to the task service before commenting.');
    const taskId = parseTaskId(payload.taskId);
    if (taskId == null) throw new Error('A valid task ID is required.');
    const text = payload.text.trim();
    if (!text) throw new Error('Enter a comment.');
    if (pendingChange.current) throw new Error('Wait for the current task change to finish.');
    const operation = {
      taskId,
      controller: new AbortController(),
      error: undefined as Error | undefined,
    };
    pendingChange.current = operation;
    setPendingTaskIds([taskId]);
    try {
      // Unlike task updates, comment creation sends confirmation to its creator.
      await new Promise<void>((resolve, reject) => {
        const cleanup = () => {
          clearTimeout(timeout);
          socket.off('comment:create', onCreated);
          operation.controller.signal.removeEventListener('abort', onAbort);
        };
        const onAbort = () => {
          cleanup();
          reject(operation.error || new Error('Unable to post the comment.'));
        };
        const onCreated = (data: TaskEventData & { text?: string; comment_id?: unknown }) => {
          // The creator receives only { comment_id }; broadcasts may also include
          // task/text. Only one operation is pending, so the creator response
          // confirms this request. Check optional fields when supplied.
          const responseTaskId = data?.task_id ?? data?.taskId;
          if (
            parseTaskId(data?.comment_id) == null ||
            (responseTaskId !== undefined && parseTaskId(responseTaskId) !== taskId) ||
            (data?.text !== undefined && data.text !== text)
          ) return;
          cleanup();
          resolve();
        };
        const timeout = setTimeout(() => {
          operation.error = new Error(
            'The task service did not confirm the comment. Check before trying again to avoid duplicates.'
          );
          operation.controller.abort();
        }, 10000);
        socket.on('comment:create', onCreated);
        operation.controller.signal.addEventListener('abort', onAbort);
        try {
          socket.emit('comment:create', { taskId, text });
        } catch (error) {
          cleanup();
          reject(error);
        }
      });
      refreshTasks({ taskId });
    } finally {
      if (pendingChange.current === operation) {
        pendingChange.current = null;
        setPendingTaskIds([]);
      }
    }
  };

  return (
    <TaskContext.Provider
      value={{
        socket,
        connected,
        connectionError,
        pendingTaskIds,
        setSelectedTaskId,
        updateTask,
        assignTask,
        createComment,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTaskContext = () => {
  const context = useContext(TaskContext);
  if (!context) throw new Error('TaskContextProvider is required.');
  return context;
};
